// The one-shot session-expiry latch (round #101): the api client's fetch wrapper
// raises the event on any intercepted 401; the top-level listener owns the router
// and navigates. The latch collapses N concurrent 401s into ONE redirect and is
// re-armed by the listener when a navigation completes.

export const SESSION_EXPIRED_EVENT = 'tvstor:session-expired'

// The one endpoint that answers ABOUT the session itself — the authority the
// interceptor asks before believing a 401 (#87, 2026-08-09).
export const SESSION_PROBE_PATH = '/api/v1/auth/sessions/current'

// Requests whose 401 is a LEGITIMATE form answer — a wrong password typed into a
// form — not an expired session: the login itself and the sudo elevation prompt.
const CREDENTIAL_PROBES = [
  { method: 'POST', path: '/api/v1/auth/sessions' },
  { method: 'POST', path: '/api/v1/auth/sudo' }
]

export type ExpiryVerdict = 'ignore' | 'probe' | 'expired'

export type ExpiryInput = {
  status: number
  pathname: string
  method: string

  // The Authorization header the request actually carried, verbatim.
  authorization: string | null

  // The console session's token, or null when there is no session at all.
  sessionToken: string | null

  // Where the browser is right now — /login must never redirect to itself.
  currentPath: string
}

// Il verdetto del gestore 401 globale, come funzione PURA (#87, caccia 3,
// ratifica Luca 2026-08-09).
//
// Il difetto: il gestore era raggiungibile A SESSIONE VIVA. Un 401 su una
// richiesta di contorno — un token d'editor scaduto, una risorsa con
// credenziale propria, una chiamata di sfondo — buttava via la sessione e con
// essa il lavoro aperto nei form dell'operatore. Un singolo 401 non e' una
// prova che la sessione sia morta: e' un sintomo, e riguarda LA RICHIESTA che
// l'ha ricevuto.
//
// Tre esiti, non due:
//   ignore  — questo 401 non dice nulla sulla sessione;
//   probe   — potrebbe dirlo: si interroga l'autorita' prima di agire;
//   expired — l'autorita' stessa ha risposto 401: la sessione non c'e' piu'.
export function expiryVerdictOf(input: ExpiryInput): ExpiryVerdict {
  if (input.status !== 401 || input.currentPath === '/login') {
    return 'ignore'
  }

  if (CREDENTIAL_PROBES.some(probe => probe.method === input.method.toUpperCase() && probe.path === input.pathname)) {
    return 'ignore'
  }

  if (input.sessionToken === null) {
    // Nessuna sessione da perdere: distruggerla sarebbe distruggere il nulla e
    // portare l'utente a /login dal posto in cui gia' e'.
    return 'ignore'
  }

  if (input.pathname === SESSION_PROBE_PATH) {
    // La sonda E' l'autorita': un suo 401 e' la risposta, non un sintomo.
    return 'expired'
  }

  if (input.authorization !== `Bearer ${input.sessionToken}`) {
    // La richiesta portava un'ALTRA credenziale (o nessuna): il suo 401
    // riguarda quella, non la sessione della console.
    return 'ignore'
  }

  return 'probe'
}

let notified = false

export function notifySessionExpired() {
  if (notified) {
    return
  }

  notified = true
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT))
}

export function resetSessionExpiredLatch() {
  notified = false
}
